import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { useProfileSetup } from "@/hooks/useProfileSetup";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import PreviewNetworkBanner from "./components/PreviewNetworkBanner";

const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const AppHome = lazy(() => import("./pages/AppHome"));
const MyProperties = lazy(() => import("./pages/MyProperties"));
const NewProperty = lazy(() => import("./pages/NewProperty"));
const Matches = lazy(() => import("./pages/Matches"));
const Messages = lazy(() => import("./pages/Messages"));
const Chat = lazy(() => import("./pages/Chat"));
const Profile = lazy(() => import("./pages/Profile"));
const NegotiationPanel = lazy(() => import("./pages/NegotiationPanel"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminProperties = lazy(() => import("./pages/admin/AdminProperties"));
const AdminNegotiations = lazy(() => import("./pages/admin/AdminNegotiations"));
const AdminNegotiationDocs = lazy(() => import("./pages/admin/AdminNegotiationDocs"));
const AdminBrokers = lazy(() => import("./pages/admin/AdminBrokers"));
const AdminServices = lazy(() => import("./pages/admin/AdminServices"));
const BrokerDashboard = lazy(() => import("./pages/BrokerDashboard"));
const BrokerClients = lazy(() => import("./pages/BrokerClients"));
const BrokerSearch = lazy(() => import("./pages/BrokerSearch"));
const BrokerLanding = lazy(() => import("./pages/BrokerLanding"));
const CockpitLayout = lazy(() => import("./pages/broker/CockpitLayout"));
const CockpitDashboard = lazy(() => import("./pages/broker/CockpitDashboard"));
const CockpitProperties = lazy(() => import("./pages/broker/CockpitProperties"));
const CockpitOwners = lazy(() => import("./pages/broker/CockpitOwners"));
const CockpitVisits = lazy(() => import("./pages/broker/CockpitVisits"));
const CockpitProposals = lazy(() => import("./pages/broker/CockpitProposals"));
const CockpitFollowups = lazy(() => import("./pages/broker/CockpitFollowups"));
const CockpitSwaps = lazy(() => import("./pages/broker/CockpitSwaps"));
const CockpitReports = lazy(() => import("./pages/broker/CockpitReports"));
const CockpitCapture = lazy(() => import("./pages/broker/CockpitCapture"));
const SwapEngine = lazy(() => import("./pages/SwapEngine"));
const PitchDeck = lazy(() => import("./pages/PitchDeck"));
const EditProperty = lazy(() => import("./pages/EditProperty"));
const CompareProperties = lazy(() => import("./pages/CompareProperties"));
const PublicProperty = lazy(() => import("./pages/PublicProperty"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail"));
const BrokerBulkImport = lazy(() => import("./pages/BrokerBulkImport"));
const PacoteJuridico = lazy(() => import("./pages/PacoteJuridico"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const PropertyServices = lazy(() => import("./pages/PropertyServices"));
const PartnersMarketplace = lazy(() => import("./pages/PartnersMarketplace"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners"));
const AdminBilling = lazy(() => import("./pages/admin/AdminBilling"));
const AdminPricing = lazy(() => import("./pages/admin/AdminPricing"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminFeedback = lazy(() => import("./pages/admin/AdminFeedback"));
const AdminDocPendencies = lazy(() => import("./pages/admin/AdminDocPendencies"));
const AdminEmailHealth = lazy(() => import("./pages/admin/AdminEmailHealth"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const BuscarImoveis = lazy(() => import("./pages/BuscarImoveis"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const SecurityPage = lazy(() => import("./pages/Security"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const NotificationsCenter = lazy(() => import("./pages/NotificationsCenter"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile"));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
  </div>
);

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const { needsSetup, loading: setupLoading } = useProfileSetup();
  const location = useLocation();
  if (loading || setupLoading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (needsSetup && location.pathname !== "/completar-cadastro") return <Navigate to="/completar-cadastro" replace />;
  return <>{children}</>;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  
  if (authLoading || adminLoading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/app" replace />;
  return <AdminLayout>{children}</AdminLayout>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <PreviewNetworkBanner />
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/buscar" element={<ProtectedRoute><SearchResults /></ProtectedRoute>} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/completar-cadastro" element={<CompleteProfile />} />
              <Route path="/corretores" element={<BrokerLanding />} />
              <Route path="/pitch" element={<PitchDeck />} />
              <Route path="/pacote-juridico" element={<PacoteJuridico />} />
              <Route path="/parceiros" element={<PartnersMarketplace />} />
              <Route path="/app" element={<ProtectedRoute><AppHome /></ProtectedRoute>} />
              <Route path="/app/engine" element={<ProtectedRoute><SwapEngine /></ProtectedRoute>} />
              <Route path="/app/properties" element={<ProtectedRoute><MyProperties /></ProtectedRoute>} />
              <Route path="/app/buscar" element={<ProtectedRoute><BuscarImoveis /></ProtectedRoute>} />
              <Route path="/app/properties/new" element={<ProtectedRoute><NewProperty /></ProtectedRoute>} />
              <Route path="/app/properties/:id/edit" element={<ProtectedRoute><EditProperty /></ProtectedRoute>} />
              <Route path="/app/properties/:id/services" element={<ProtectedRoute><PropertyServices /></ProtectedRoute>} />
              <Route path="/app/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
              <Route path="/app/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
              <Route path="/app/messages/:matchId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
              <Route path="/app/notifications" element={<ProtectedRoute><NotificationsCenter /></ProtectedRoute>} />
              <Route path="/app/negotiation/:matchId" element={<ProtectedRoute><NegotiationPanel /></ProtectedRoute>} />
              <Route path="/app/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/app/broker" element={<ProtectedRoute><BrokerDashboard /></ProtectedRoute>} />
              <Route path="/app/broker/clients" element={<ProtectedRoute><BrokerClients /></ProtectedRoute>} />
              <Route path="/app/broker/search" element={<ProtectedRoute><BrokerSearch /></ProtectedRoute>} />
              <Route path="/app/compare" element={<ProtectedRoute><CompareProperties /></ProtectedRoute>} />
              <Route path="/app/broker/import" element={<ProtectedRoute><BrokerBulkImport /></ProtectedRoute>} />
              
              {/* Cockpit do Corretor */}
              <Route path="/app/broker/cockpit" element={<ProtectedRoute><CockpitLayout><CockpitDashboard /></CockpitLayout></ProtectedRoute>} />
              <Route path="/app/broker/cockpit/imoveis" element={<ProtectedRoute><CockpitLayout><CockpitProperties /></CockpitLayout></ProtectedRoute>} />
              <Route path="/app/broker/cockpit/captacao" element={<ProtectedRoute><CockpitLayout><CockpitCapture /></CockpitLayout></ProtectedRoute>} />
              <Route path="/app/broker/cockpit/proprietarios" element={<ProtectedRoute><CockpitLayout><CockpitOwners /></CockpitLayout></ProtectedRoute>} />
              <Route path="/app/broker/cockpit/visitas" element={<ProtectedRoute><CockpitLayout><CockpitVisits /></CockpitLayout></ProtectedRoute>} />
              <Route path="/app/broker/cockpit/propostas" element={<ProtectedRoute><CockpitLayout><CockpitProposals /></CockpitLayout></ProtectedRoute>} />
              <Route path="/app/broker/cockpit/followups" element={<ProtectedRoute><CockpitLayout><CockpitFollowups /></CockpitLayout></ProtectedRoute>} />
              <Route path="/app/broker/cockpit/permutas" element={<ProtectedRoute><CockpitLayout><CockpitSwaps /></CockpitLayout></ProtectedRoute>} />
              <Route path="/app/broker/cockpit/relatorios" element={<ProtectedRoute><CockpitLayout><CockpitReports /></CockpitLayout></ProtectedRoute>} />
              <Route path="/app/property/:id" element={<ProtectedRoute><PropertyDetail /></ProtectedRoute>} />
              <Route path="/imovel/:id" element={<PublicProperty />} />
              
              {/* Admin routes */}
              <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
              <Route path="/admin/properties" element={<AdminRoute><AdminProperties /></AdminRoute>} />
              <Route path="/admin/negotiations" element={<AdminRoute><AdminNegotiations /></AdminRoute>} />
              <Route path="/admin/negotiation-docs" element={<AdminRoute><AdminNegotiationDocs /></AdminRoute>} />
              <Route path="/admin/brokers" element={<AdminRoute><AdminBrokers /></AdminRoute>} />
              <Route path="/admin/services" element={<AdminRoute><AdminServices /></AdminRoute>} />
              <Route path="/admin/partners" element={<AdminRoute><AdminPartners /></AdminRoute>} />
              <Route path="/admin/billing" element={<AdminRoute><AdminBilling /></AdminRoute>} />
              <Route path="/admin/pricing" element={<AdminRoute><AdminPricing /></AdminRoute>} />
              <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
              <Route path="/admin/feedback" element={<AdminRoute><AdminFeedback /></AdminRoute>} />
              <Route path="/admin/pendencias" element={<AdminRoute><AdminDocPendencies /></AdminRoute>} />
              <Route path="/admin/email-health" element={<AdminRoute><AdminEmailHealth /></AdminRoute>} />
              
              <Route path="/central-de-ajuda" element={<HelpCenter />} />
              <Route path="/termos-de-uso" element={<TermsOfUse />} />
              <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
              <Route path="/seguranca" element={<SecurityPage />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
